import React, { useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert, Box, Button, CircularProgress, Divider, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import { DEFAULT_API_ADMIN } from '../../../constants';
import { getChainId, httpClientReactAdmin } from '../../../lib';
import {
  RoletaSegment, RoletaSegmentKind, RoletaSimulationResult, RoletaSimulationSegment,
} from '../../../models/roleta';

const DEFAULT_DRAWS = 1000;

const fmtNum = (value: number) => value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const fmtPct = (value: number) => `${(value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const fmtBrl = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Props {
  roletaId: string;
  segments: RoletaSegment[];
  /** Bloqueia o botão quando o cadastro ainda não fecha (fatias com erro). */
  disabled?: boolean;
}

interface Row {
  key: string;
  label: string;
  kind?: RoletaSegmentKind;
  expectedPct: number;
  simulatedPct: number;
  count: number;
  unitCost: number;
  totalCost: number;
  maxWins: number | null;
  shortStock: boolean;
}

/**
 * "Simular 1.000 giros" — `POST /chains/{chainId}/roletas/{id}/simulate`, sem
 * efeito colateral nenhum: só devolve a distribuição e o custo esperado.
 *
 * Tudo que o backend não mandar é calculado aqui pelos pesos, para o painel
 * nunca aparecer vazio.
 */
const SimulationPanel: React.FC<Props> = ({ roletaId, segments, disabled }) => {
  const notify = useNotify();
  const chainId = getChainId();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RoletaSimulationResult | null>(null);
  const [expectedSpins, setExpectedSpins] = useState<string>('');

  const enabledSegments = useMemo(() => segments.filter((segment) => segment.isEnabled), [segments]);
  const totalWeight = useMemo(
    () => enabledSegments.reduce((sum, segment) => sum + Math.max(0, segment.weight || 0), 0),
    [enabledSegments],
  );

  const findConfigured = (row: RoletaSimulationSegment): RoletaSegment | undefined =>
    segments.find((segment) => (row.segmentId && segment.id === row.segmentId) || segment.segmentIndex === row.segmentIndex);

  const rows: Row[] = useMemo(() => {
    if (!result?.segments?.length) return [];
    const draws = result.draws || DEFAULT_DRAWS;

    return result.segments.map((row) => {
      const configured = findConfigured(row);
      const weight = row.weight ?? configured?.weight ?? 0;
      const expectedPct = row.expectedPct ?? (totalWeight > 0 ? weight / totalWeight : 0);
      const count = row.count ?? 0;
      const simulatedPct = row.simulatedPct ?? (draws > 0 ? count / draws : 0);
      const unitCost = row.unitCost ?? configured?.estimatedCost ?? 0;
      const totalCost = row.totalCost ?? unitCost * count;
      const maxWins = row.maxWins ?? configured?.maxWins ?? null;

      return {
        key: row.segmentId ?? `segment-${row.segmentIndex}`,
        label: row.label ?? configured?.label ?? `Fatia ${row.segmentIndex + 1}`,
        kind: row.kind ?? configured?.kind,
        expectedPct,
        simulatedPct,
        count,
        unitCost,
        totalCost,
        maxWins,
        shortStock: (row.kind ?? configured?.kind) === RoletaSegmentKind.Prize
          && maxWins !== null
          && maxWins !== undefined
          && count > maxWins,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, segments, totalWeight]);

  const totalCost = useMemo(
    () => result?.totalCost ?? rows.reduce((sum, row) => sum + row.totalCost, 0),
    [result, rows],
  );

  const costPerSpin = useMemo(() => {
    if (result?.expectedCostPerSpin !== undefined && result?.expectedCostPerSpin !== null) {
      return result.expectedCostPerSpin;
    }
    const draws = result?.draws || DEFAULT_DRAWS;
    return draws > 0 ? totalCost / draws : 0;
  }, [result, totalCost]);

  const plannedSpins = Number(expectedSpins.replace(/\D/g, '')) || 0;
  const shortStockRows = rows.filter((row) => row.shortStock);

  const handleSimulate = async () => {
    setLoading(true);
    try {
      const url = `${DEFAULT_API_ADMIN}/chains/${chainId}/roletas/${roletaId}/simulate`;
      const { json } = await httpClientReactAdmin(url, {
        method: 'POST',
        body: JSON.stringify({ draws: DEFAULT_DRAWS }),
      });
      setResult((json?.data ?? json) as RoletaSimulationResult);
    } catch (err: any) {
      const apiMessage = err?.body?.message || err?.body?.error || err?.message || 'Erro ao simular';
      notify(apiMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Simular 1.000 giros</Typography>
          <Typography variant="caption" color="text.secondary">
            Roda a mesma matemática do sorteio, sem entregar nada a ninguém. Serve para você ver o custo antes de ativar.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <ScienceIcon />}
          onClick={handleSimulate}
          disabled={loading || disabled}
        >
          Simular
        </Button>
      </Stack>

      {disabled && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Salve as fatias para simular com a configuração de verdade.
        </Alert>
      )}

      {!result && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Nenhuma simulação ainda.
        </Typography>
      )}

      {result && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fatia</TableCell>
                  <TableCell align="center">% configurado</TableCell>
                  <TableCell align="center">% simulado</TableCell>
                  <TableCell align="center">Vezes</TableCell>
                  <TableCell align="center">Custo unitário</TableCell>
                  <TableCell align="center">Custo total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      {row.label}
                      {row.kind && row.kind !== RoletaSegmentKind.Prize && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {RoletaSegmentKind.translateKind(row.kind)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{fmtPct(row.expectedPct)}</TableCell>
                    <TableCell align="center">{fmtPct(row.simulatedPct)}</TableCell>
                    <TableCell align="center">{fmtNum(row.count)}</TableCell>
                    <TableCell align="center">{row.unitCost ? fmtBrl(row.unitCost) : '—'}</TableCell>
                    <TableCell align="center">{row.totalCost ? fmtBrl(row.totalCost) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {shortStockRows.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Estoque menor que o esperado em {shortStockRows.length === 1 ? 'uma fatia' : `${shortStockRows.length} fatias`}:{' '}
              {shortStockRows.map((row) => row.label).join(', ')}. A fatia sai do sorteio quando o estoque acaba.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Custo esperado por giro</Typography>
              <Typography variant="h6" fontWeight={700}>{fmtBrl(costPerSpin)}</Typography>
            </Box>
            <TextField
              size="small"
              label="Giros previstos na campanha"
              value={expectedSpins}
              onChange={(event) => setExpectedSpins(event.target.value)}
              inputProps={{ inputMode: 'numeric' }}
              helperText="Quantos giros você espera distribuir"
              sx={{ width: 220 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Custo esperado da campanha</Typography>
              <Typography variant="h6" fontWeight={700}>
                {plannedSpins > 0 ? fmtBrl(costPerSpin * plannedSpins) : '—'}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default SimulationPanel;
